import { Body2 } from 'stories/elements/Typography/Text'
import { Know } from 'interfaces/Know'
import { AnalyticsEventBreadcrumbClick } from 'lib/constants/AnalyticsEventType'
import theme from 'stories/utils/theme'
import { trackEvent } from 'lib/tracking/analytics-service'
import Link from 'next/link'
import styled, { css } from 'styled-components'
import Row from 'components/elements/Row'
import React from 'react'
import ArrowRightIcon from 'assets/png-preview/chevron-right.svg'

const BreadcrumbsContainer = styled.div<{ addBorderTop?: boolean; minimalizeBreadCrumbs?: boolean; scrollable?: boolean }>`
    ${({ minimalizeBreadCrumbs, scrollable }) => css`
        svg {
            position: relative;
            top: 2px;
        }
        display: flex;
        padding-bottom: 10px;
        gap: 8px;
        flex-wrap: wrap;
        p,
        a {
            font-size: 12px;
            line-height: 14px;
            letter-spacing: 0.6px;
        }
        @media (max-width: 1050px) {
            padding: ${minimalizeBreadCrumbs ? '0px' : '10px 15px'};
            gap: 10px;
            flex-wrap: ${scrollable ? 'nowrap' : 'wrap'};
            p,
            a {
                font-size: ${minimalizeBreadCrumbs ? 12 : 14}px;
                line-height: ${minimalizeBreadCrumbs ? 10 : 14}px;
            }
            a > p:hover {
                color: ${theme.colors.deepSeaBlue};
            }
            ${scrollable &&
            css`
                overflow-x: scroll;
                max-width: 90vw;
                p {
                    white-space: nowrap;
                }
            `}
        }
    `};
`

interface Props {
    breadcrumbs: {
        label: string
        href: string
        isNotALink?: boolean
    }[]
    know?: Know
    referrerScreen?: string
    addBorderTop?: boolean
    minimalizeBreadCrumbs?: boolean
    scrollable?: boolean
    showArrows?: boolean
}

const Breadcrumbs = ({
    breadcrumbs,
    know,
    referrerScreen,
    addBorderTop,
    minimalizeBreadCrumbs,
    scrollable,
    showArrows,
}: Props) => {
    const onBreadcrumbClick = (link: string) => {
        trackEvent(AnalyticsEventBreadcrumbClick, { value: link, know, referrerScreen })
    }

    return (
        <BreadcrumbsContainer
            minimalizeBreadCrumbs={minimalizeBreadCrumbs}
            addBorderTop={addBorderTop}
            scrollable={scrollable}
            data-nosnippet="true"
            className="breadcrumbs"
        >
            {breadcrumbs?.length
                ? breadcrumbs.map((breadcrumb, count) => (
                      <React.Fragment key={breadcrumb.label}>
                          {breadcrumb.isNotALink ? (
                              <Body2
                                  color={
                                      count === breadcrumbs.length - 1 ? theme.colors.white : theme.colors.grayScaleSecondary
                                  }
                                  fontFamily="var(--font-inter)"
                                  fontWeight={count === breadcrumbs.length - 1 ? 550 : 400}
                                  fontSize={15}
                                  key={breadcrumb.label}
                              >
                                  {`${breadcrumb.label}`}
                              </Body2>
                          ) : (
                              <Row key={breadcrumb.label} gap={8}>
                                  <Link href={breadcrumb.href} prefetch={false}>
                                      <Body2
                                          color={
                                              count === breadcrumbs.length - 1
                                                  ? theme.colors.white
                                                  : theme.colors.grayScaleSecondary
                                          }
                                          fontWeight={count === breadcrumbs.length - 1 ? 550 : 400}
                                          fontFamily="var(--font-inter)"
                                          fontSize={15}
                                          key={breadcrumb.label}
                                          onClick={() => onBreadcrumbClick(breadcrumb.href)}
                                      >
                                          {breadcrumb.label}
                                      </Body2>
                                  </Link>
                                  <Body2 fontWeight={400} fontFamily="var(--font-inter)" fontSize={15}>
                                      {++count !== breadcrumbs.length ? showArrows ? <ArrowRightIcon /> : ' /' : ''}
                                  </Body2>
                              </Row>
                          )}
                      </React.Fragment>
                  ))
                : ''}
        </BreadcrumbsContainer>
    )
}

export default Breadcrumbs
